import { SaveOutlined } from '@ant-design/icons';
import { Alert, Input } from 'antd';
import { Form, Formik } from 'formik';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Sort } from 'src/icons/Sort.icon';
import { getAllConditionListAction } from 'src/modules/SuperAdmin/store/actions';
import { getAllActiveOrganizationAction } from 'src/modules/SuperAdmin/store/actions/organization.action';
import { getOrganizationByIdSlice } from 'src/modules/SuperAdmin/store/slices/organization.slice';
import {
  getLookbackPeriods,
  updateMappings,
} from 'src/store/actions/appointment.action';
import { getOrgSetting } from 'src/store/actions/settings.action';
import { getFromStorage } from '../../../../../../lib/storage';
import organizationEventEmitter from '../../../../../../utilities/organizationEmmiter';
import { SuperAdminTable } from '../../../baseComponents/Table';

const LookbackPeriodPage = () => {
  const dispatch = useDispatch();

  const { getActiveOrganizationsList } = useSelector(
    (state) => state?.organizationState
  );
  const { currentPractitioner } = useSelector(
    (state) => state?.practitionerState
  );
  const { lookbackPeriods, lookbackPeriodsLoading } = useSelector(
    (state) => state?.appointmentState
  );
  const { getAllConditionsList, getAllConditionsListLoading } = useSelector(
    (state) => state?.conditionState
  );
  const [lookbackPeriod, setLookbackPeriod] = useState(null);
  const [tableData, setTableData] = useState(null);
  const [tableLoader, setTableLoader] = useState(false);
  const [bulkCSVUploadEnabled, setBulkCSVUploadEnabled] = useState(
    currentPractitioner?.org_settings?.enable_bulk_appointment_upload ?? false
  );
  const [organizationDropdown, setOrganizationDropdown] = useState([]);
  const [selectedOrganization, setSelectedOrganization] = useState(() => {
    const savedOrg = getFromStorage('superAdminSelectedOrganization');
    const parsedSavedOrg = savedOrg ? JSON.parse(savedOrg) : null;
    return parsedSavedOrg?.org_uuid || currentPractitioner?.org_uuid || null;
  });

  const [organizationName, setOrganizationName] = useState(null);
  const [organization, setOrganization] = useState(currentPractitioner?.org_id);
  const [updatedValues, setUpdatedValues] = useState({});

  useEffect(() => {
    setTableLoader(getAllConditionsListLoading && lookbackPeriodsLoading);
  }, [getAllConditionsListLoading, lookbackPeriodsLoading]);

  useEffect(() => {
    dispatch(getAllConditionListAction());
    dispatch(getAllActiveOrganizationAction());
    dispatch(getOrganizationByIdSlice(null));
  }, [dispatch]);

  useEffect(() => {
    if (selectedOrganization) {
      dispatch(getLookbackPeriods(selectedOrganization));
      dispatch(getAllConditionListAction());
    }
  }, [selectedOrganization]);

  useEffect(() => {
    if (
      getActiveOrganizationsList &&
      Array.isArray(getActiveOrganizationsList)
    ) {
      let dataArr = [];
      getActiveOrganizationsList.forEach((key, index) => {
        dataArr.push({
          value: key?.org_id,
          label: key?.org_name,
          org_uuid: key?.org_uuid,
        });
      });
      setOrganizationDropdown(dataArr);
    }
  }, [getActiveOrganizationsList]);

  useEffect(() => {
    if (
      getAllConditionsList &&
      Array.isArray(getAllConditionsList) &&
      getAllConditionsList.length > 0
    ) {
      const initialValues = {};
      getAllConditionsList.forEach((condition) => {
        initialValues[condition.condition_name] =
          lookbackPeriod && lookbackPeriod[condition.condition_name]
            ? lookbackPeriod[condition.condition_name]
            : 0;
      });
      setUpdatedValues(initialValues);
    }
  }, [getAllConditionsList, lookbackPeriod, selectedOrganization]);

  useEffect(() => {
    if (
      getAllConditionsList &&
      Array.isArray(getAllConditionsList) &&
      getAllConditionsList.length > 0
    ) {
      let dataArray = getAllConditionsList?.map((list, index) => {
        return {
          key: index,
          condition_name: list?.condition_name,
        };
      });
      if (dataArray && Array.isArray(dataArray) && dataArray.length > 0) {
        setTableData(dataArray);
      }
    } else {
      setTableData([]);
    }
  }, [getAllConditionsList, selectedOrganization]);

  useEffect(() => {
    setLookbackPeriod(lookbackPeriods);
  }, [lookbackPeriods, selectedOrganization]);

  const conditionNameSorter = (a, b) => {
    return a.condition_name.localeCompare(b.condition_name);
  };

  useEffect(() => {
    async function getOrgSettingData() {
      await dispatch(getOrgSetting(selectedOrganization)).then((setting) => {
        setBulkCSVUploadEnabled(
          setting?.enable_bulk_appointment_upload ?? false
        );
      });
    }
    getOrgSettingData();
  }, [selectedOrganization, dispatch]);

  useEffect(() => {
    const unsubscribe = organizationEventEmitter.subscribe(
      'organizationChanged',
      (newOrg) => {
        setSelectedOrganization(newOrg);
        handleSelectChange(newOrg);
      }
    );

    return () => unsubscribe();
  }, []);

  const handleSelectChange = (option) => {
    const orgId = option?.org_uuid;
    setSelectedOrganization(orgId);
    setOrganizationName(option);
    setOrganization(option?.value);
  };

  const handleInputChange = (e, conditionName) => {
    setUpdatedValues({
      ...updatedValues,
      [conditionName]: parseInt(e.target.value),
    });
  };

  const handleFormSubmit = (values) => {
    let payload = {
      settings: {
        lookback_period: updatedValues,
      },
    };
    dispatch(updateMappings(payload, selectedOrganization, 'lookback period'));
  };

  const columns = [
    {
      title: (
        <span className="font-[700]">
          {' '}
          Condition name{' '}
          {tableData?.length > 0 && (
            <Sort className={'sort-icon-lookback-period'} />
          )}
        </span>
      ),
      dataIndex: 'condition_name',
      key: 'condition_name',
      sorter: tableData?.length > 0 && conditionNameSorter,
      width: '50%',
    },
    {
      title: <span className="font-[700]"> Lookback period (in days)</span>,
      render: (text, record) => {
        return (
          <>
            <div className="flex flex-row">
              <div className="basis-1/2">
                <Input
                  type="number"
                  value={
                    updatedValues[record.condition_name] !== undefined
                      ? updatedValues[record.condition_name]
                      : lookbackPeriod && lookbackPeriod[record.condition_name]
                        ? lookbackPeriod[record.condition_name]
                        : 0
                  }
                  onChange={(e) => {
                    handleInputChange(e, record.condition_name);
                  }}
                  onBlur={(e) => {
                    if (e.target.value.trim() === '') {
                      const originalValue =
                        lookbackPeriod && lookbackPeriod[record.condition_name]
                          ? lookbackPeriod[record.condition_name]
                          : 0;
                      setUpdatedValues({
                        ...updatedValues,
                        [record.condition_name]: originalValue,
                      });
                    }
                  }}
                />
              </div>
            </div>
          </>
        );
      },
      width: '50%',
    },
  ];

  return (
    <>
      <div>
        <Formik initialValues={{}} onSubmit={handleFormSubmit}>
          {({ handleSubmit }) => (
            <Form onSubmit={handleSubmit}>
              <div className="flex flex-row sm:justify-between gap-4 lg:justify-between items-center">
                <div className="flex flex-col justify-center">
                  <div className="flex justify-start gap-12 "></div>
                </div>
                {bulkCSVUploadEnabled && (
                  <div className="flex justify-end items-end">
                    <button
                      type="submit"
                      className="bg-[#00D090] hover:bg-[#059669] flex items-center justify-center text-white font-normal p-2 py-2 px-4 rounded-xl focus:outline-none focus:shadow-outline"
                    >
                      <SaveOutlined
                        className={`mr-2`}
                        width="14"
                        height="14"
                        viewBox="0 0 16 16"
                      />{' '}
                      Save all
                    </button>
                  </div>
                )}
              </div>

              {bulkCSVUploadEnabled ? (
                <div className="super-admin-table">
                  <div className="mt-5 ant-table rounded-xl shadow-xl">
                    <SuperAdminTable
                      className={`appointment-page-table`}
                      columns={columns}
                      data={tableData}
                      loading={tableLoader}
                    />
                  </div>
                </div>
              ) : (
                <div className="flex justify-center items-center p-4 min-h-[60vh]">
                  <Alert
                    message="Bulk CSV uploads are currently disabled for this organization."
                    type="info"
                    showIcon
                  />
                </div>
              )}
            </Form>
          )}
        </Formik>
      </div>
    </>
  );
};

export default LookbackPeriodPage;
